import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Clear as ClearIcon,
    Undo as UndoIcon,
    Redo as RedoIcon
} from '@mui/icons-material';

/**
 * SignaturePad Component
 *
 * A production-ready signature capture component with the following features:
 * - Canvas-based signature drawing
 * - Clear, Undo, Redo functionality
 * - Responsive design (mobile & desktop)
 * - Base64 export for backend storage
 * - Touch & mouse support
 * - Empty signature validation
 */
const SignaturePad = ({ onSave, onCancel, signerName = "Signer", loading = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const sigPadRef = useRef(null);

    // State for undo/redo functionality
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);
    const [isEmpty, setIsEmpty] = useState(true);

    // Handle signature start (first stroke)
    const handleBegin = () => {
        if (isEmpty) {
            setIsEmpty(false);
        }
    };

    // Handle signature end (save to history for undo/redo)
    const handleEnd = () => {
        if (sigPadRef.current) {
            const currentData = sigPadRef.current.toDataURL();
            const newHistory = history.slice(0, historyStep + 1);
            newHistory.push(currentData);
            setHistory(newHistory);
            setHistoryStep(newHistory.length - 1);
        }
    };

    // Clear the signature pad
    const handleClear = () => {
        if (sigPadRef.current) {
            sigPadRef.current.clear();
            setIsEmpty(true);
            setHistory([]);
            setHistoryStep(-1);
        }
    };

    // Undo last stroke
    const handleUndo = () => {
        if (historyStep > 0) {
            const newStep = historyStep - 1;
            setHistoryStep(newStep);

            if (newStep === -1) {
                sigPadRef.current.clear();
                setIsEmpty(true);
            } else {
                sigPadRef.current.fromDataURL(history[newStep]);
                setIsEmpty(false);
            }
        } else if (historyStep === 0) {
            setHistoryStep(-1);
            sigPadRef.current.clear();
            setIsEmpty(true);
        }
    };

    // Redo stroke
    const handleRedo = () => {
        if (historyStep < history.length - 1) {
            const newStep = historyStep + 1;
            setHistoryStep(newStep);
            sigPadRef.current.fromDataURL(history[newStep]);
            setIsEmpty(false);
        }
    };

    // Save signature as Base64
    const handleSave = () => {
        console.log('SignaturePad handleSave called');
        console.log('sigPadRef current:', sigPadRef.current ? 'exists' : 'null');
        console.log('isEmpty:', sigPadRef.current ? sigPadRef.current.isEmpty() : 'n/a');

        if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
            const signatureData = sigPadRef.current.toDataURL('image/png');
            console.log('Signature data generated, length:', signatureData.length);
            console.log('Calling onSave callback');
            onSave(signatureData);
        } else {
            // Show error if signature is empty
            console.warn('Signature is empty or sigPadRef is null');
            alert('Please provide a signature before saving.');
        }
    };

    // Canvas dimensions
    const canvasWidth = isMobile ? 320 : 600;
    const canvasHeight = isMobile ? 200 : 300;

    return (
        <Box>
            {/* Instructions */}
            <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#398DD2', mb: 1 }}>
                    Sign Below
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {signerName}, please draw your signature in the box below
                </Typography>
            </Box>

            {/* Signature Canvas */}
            <Paper
                elevation={3}
                sx={{
                    display: 'inline-block',
                    border: '2px solid #398DD2',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    mb: 2
                }}
            >
                <SignatureCanvas
                    ref={sigPadRef}
                    canvasProps={{
                        width: canvasWidth,
                        height: canvasHeight,
                        className: 'signature-canvas',
                        style: {
                            backgroundColor: '#ffffff',
                            cursor: 'crosshair',
                            touchAction: 'none' // Prevent scrolling on touch devices
                        }
                    }}
                    minWidth={0.5}
                    maxWidth={2.5}
                    dotSize={1}
                    throttle={16}
                    velocityFilterWeight={0.7}
                    onBegin={handleBegin}
                    onEnd={handleEnd}
                />

                {/* Watermark when empty */}
                {isEmpty && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            opacity: 0.3
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#ccc',
                                fontFamily: 'cursive',
                                userSelect: 'none'
                            }}
                        >
                            Sign Here
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Controls */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
            }}>
                {/* Left side - Edit controls */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Clear Signature">
                        <IconButton
                            onClick={handleClear}
                            disabled={isEmpty}
                            sx={{
                                border: '1px solid #d32f2f',
                                color: '#d32f2f',
                                '&:hover': {
                                    bgcolor: 'rgba(211, 47, 47, 0.08)'
                                },
                                '&:disabled': {
                                    borderColor: '#ccc',
                                    color: '#ccc'
                                }
                            }}
                        >
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Undo">
                        <span>
                            <IconButton
                                onClick={handleUndo}
                                disabled={historyStep < 0}
                                sx={{
                                    border: '1px solid #398DD2',
                                    color: '#398DD2',
                                    '&:hover': {
                                        bgcolor: 'rgba(57, 141, 210, 0.08)'
                                    },
                                    '&:disabled': {
                                        borderColor: '#ccc',
                                        color: '#ccc'
                                    }
                                }}
                            >
                                <UndoIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Redo">
                        <span>
                            <IconButton
                                onClick={handleRedo}
                                disabled={historyStep >= history.length - 1}
                                sx={{
                                    border: '1px solid #398DD2',
                                    color: '#398DD2',
                                    '&:hover': {
                                        bgcolor: 'rgba(57, 141, 210, 0.08)'
                                    },
                                    '&:disabled': {
                                        borderColor: '#ccc',
                                        color: '#ccc'
                                    }
                                }}
                            >
                                <RedoIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {/* Right side - Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onCancel}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#ccc',
                            color: '#666',
                            '&:hover': {
                                borderColor: '#999',
                                bgcolor: 'rgba(0,0,0,0.04)'
                            }
                        }}
                        variant="outlined"
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleSave}
                        disabled={isEmpty || loading}
                        variant="contained"
                        sx={{
                            bgcolor: '#398DD2',
                            textTransform: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            px: 3,
                            '&:hover': {
                                bgcolor: '#2c6ba0'
                            }
                        }}
                    >
                        {loading ? 'Saving...' : 'Save Signature'}
                    </Button>
                </Box>
            </Box>

            {/* Mobile tip */}
            {isMobile && (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                >
                    Tip: Use your finger to draw your signature on touch screens
                </Typography>
            )}
        </Box>
    );
};

export default SignaturePad;
